// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';

import BusComponent from '../../BusComponent';
import LayerLibraryLegendItem from './LayerLibraryLegendItem';
import VisibilityToggle from '../../form/VisibilityToggle';
import IconMenu from '../../iconMenu/IconMenu';

/**
 * @typedef Props
 * @property {import('../../../objects/LibraryLayer').default[]} layers
 * @property {string} layerLibraryGroupId
 * @property {string} mapInstanceId
 * @property {import('../../../').LayerLibraryItemMetadata} layerGroupMetadata
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {boolean} visible
 *
 * @extends {BusComponent<Props, State>}
 */
class LayerLibraryGroup extends BusComponent {
    constructor(props, context) {
        super(props, context);

        // get default visibility state by checking if any visible layer exists
        // for any classes item
        const visible = this.props.layerGroupMetadata.classes.some(
            classesItem =>
                this.props.layers.some(
                    layer =>
                        layer.id === classesItem['layer-group-id'] &&
                        layer.visible,
                ),
        );
        this.state = {
            visible: visible,
            isUserLayer: this.props.layerLibraryGroupId === 'user-layer'
        };
    }

    onDetailsOptionClicked = () => {
        const { layerGroupMetadata } = this.props;

        this.emit('CLOSE_DROPDOWN');
        this.emit('SHOW_LAYER_LIBRARY_GROUP_DETAILS_POPUP_REQUEST', layerGroupMetadata);
    };

    onRemoveClicked = () => {
        const { mapInstanceId, layerLibraryGroupId } = this.props;

        this.emit('REMOVE_LAYER_LIBRARY_GROUP', { id: layerLibraryGroupId, mapInstanceId });
    };

    toggleUserLayerGroupVisibility = () => {
        const { mapInstanceId } = this.props;
        const { visible } = this.state;

        this.emit('UPDATE_USER_LIBRARY_LAYER_GROUP_VISIBILITY', {
            visible: !visible,
            mapInstanceId: mapInstanceId,
        });
    }

    toggleLayerGroupVisibility = () => {
        const { mapInstanceId, layerLibraryGroupId } = this.props;
        const { visible } = this.state;

        this.emit('UPDATE_LAYER_LIBRARY_GROUP_VISIBILITY', {
            id: layerLibraryGroupId,
            visible: !visible,
            mapInstanceId: mapInstanceId,
        });
    }

    toggleLayerVisibility = (e) => {
        const { mapInstanceId, layerLibraryGroupId } = this.props;
        const { isUserLayer } = this.state;
        const { id } = e;

        if (isUserLayer) {
            this.emit('UPDATE_USER_LAYER_VISIBILITY', {
                id: id,
                mapInstanceId: mapInstanceId,
            });
        }
        else {
            this.emit('UPDATE_LAYER_LIBRARY_LAYER_VISIBILITY', {
                id: id,
                groupId: layerLibraryGroupId,
                mapInstanceId: mapInstanceId,
            });
        }
    }

    onVisibilityToggle = () => {
        const { visible, isUserLayer } = this.state;

        if (isUserLayer) {
           this.toggleUserLayerGroupVisibility();
        } else {
            this.toggleLayerGroupVisibility();
        }

        this.setState({
            visible: !visible
        });
    };

    isLayerVisible = (selectedLayer) => {
        const { layers } = this.props;

        return layers.some(
            layer =>
                layer.id === selectedLayer['layer-group-id'] &&
                layer.visible,
        )
    }

    render() {
        const { intl, layers, layerLibraryGroupId, layerGroupMetadata, mapInstanceId } = this.props;
        const { visible, isUserLayer } = this.state;
        const { title, classes, disableClassesInteractivity } = layerGroupMetadata;
        const interactive = !disableClassesInteractivity || isUserLayer && layers[0].interactive;
        let legendClassesItems;
        if (isUserLayer) {
            legendClassesItems = classes
                .filter(() => {
                    const groupHasVisibleLayers = layers.some(layer => layer.visible);
                    // If layer library group classes are interactive, show them all
                    // otherwise show only those in legend who have visible layers
                    return interactive || groupHasVisibleLayers;
                })
                .map(classesItem => (
                    <LayerLibraryLegendItem
                        key={classesItem.label}
                        item={classesItem}
                        mapInstanceId={mapInstanceId}
                        interactive={interactive}
                        layerVisibility={this.isLayerVisible(classesItem)}
                        toggleLayerVisibility={this.toggleLayerVisibility}
                    />
                ));
        }
        else {
            legendClassesItems = classes
                .filter(classesItem => {
                    const groupHasVisibleLayers = layers.some(
                        layer =>
                            layer.id === classesItem['layer-group-id'] &&
                            layer.visible,
                    );
                    // If layer library group classes are interactive, show them all
                    // otherwise show only those in legend who have visible layers
                    return interactive || groupHasVisibleLayers;
                })
                .map(classesItem => (
                    <LayerLibraryLegendItem
                        key={classesItem.label}
                        item={classesItem}
                        interactive={interactive}
                        layerLibraryGroupId={layerLibraryGroupId}
                        mapInstanceId={mapInstanceId}
                        layerVisibility={this.isLayerVisible(classesItem)}
                        toggleLayerVisibility={this.toggleLayerVisibility}
                    />
                ));
        }

        const titleClasses = classNames({
            disabled: !visible,
        });

        return (
            <div className="layer-library-legend__group" data-testid="layer-library-legend-group" data-group-id={layerLibraryGroupId}>
                <div className="layer-library-legend__title">
                    <span className={titleClasses} data-testid="layer-library-legend-group-title">{title}</span>
                    <div className="layer-library-legend__container">
                        {!isUserLayer && (
                            <IconMenu
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                targetOrigin={{ vertical: 'top', horizontal: 'right' }}
                                triggerClassName="btn-icon--small"
                                iconClassName="material-icons font-20"
                                ariaLabel={intl.formatMessage({ id: 'more' })}
                                testId="layer-group-menu"
                            >
                                <div className="material-menu">
                                    <button
                                        className="material-menu__item"
                                        onClick={this.onDetailsOptionClicked}
                                    >
                                        {intl.formatMessage({ id: 'layerLibrary.moreInformation' })}
                                    </button>
                                    <button
                                        className="material-menu__item"
                                        onClick={this.onRemoveClicked}
                                        data-testid="layer-group-remove"
                                    >
                                        {intl.formatMessage({ id: 'layerLibrary.removeFromLegend' })}
                                    </button>
                                </div>
                            </IconMenu>
                        )}
                        <div className={classNames("divider divider--vertical divider--btn-separator", { 'd-none': isUserLayer })} />
                        <VisibilityToggle
                            onClick={this.onVisibilityToggle}
                            visible={visible}
                            className="geo-json-library-legend__visibility-toggle"
                            iconClassName='material-icons-outlined'
                        />
                    </div>
                </div>
                {visible && (
                    <div className="layer-library-legend__items">
                        {legendClassesItems}
                    </div>
                )}
            </div>
        );
    }
}

LayerLibraryGroup.propTypes = {
    layers: PropTypes.array.isRequired,
    layerGroupMetadata: PropTypes.object.isRequired,
    mapInstanceId: PropTypes.string.isRequired,
    layerLibraryGroupId: PropTypes.string.isRequired,
};

export default injectIntl(LayerLibraryGroup);
